import { Sidebar } from "@components/sidebar/sidebar";
import { SplashScreenManager } from "@components/splash-screen/splash-screen-manager";
import { IReactChildrenOnly } from "@custom-types/types";
import { useDocumentTitle } from "@hooks/use-document-title";
import { useErrorHandler } from "@hooks/use-error-handler";
import { useHandleWindowClose } from "@hooks/use-handle-window-close";
import { SdbCompanyLoader } from "@loaders/sdb-company-loader";
import { useInitAppAnalytics } from "@hooks/use-init-app-analytics";
import { Box } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterLuxon } from "@mui/x-date-pickers-pro/AdapterLuxon";
import { LicenseInfo } from "@mui/x-license";
import { runtimeConfig } from "@src/runtime-config";
import { useAppSelector } from "@store/store-helper";
import { sidebarSelector } from "@store/ui/ui-selector";
import { Outlet } from "react-router-dom";

/**
 * All the context providers should be added here to make them available only for those pages which are using app layout.
 *
 * @returns all the react context providers and it's initialization.
 */
function AllAppLayoutContextProviders({
  children,
}: IReactChildrenOnly): JSX.Element {
  return (
    <LocalizationProvider dateAdapter={AdapterLuxon}>
      <SdbCompanyLoader>
        {children}
      </SdbCompanyLoader>
    </LocalizationProvider>
  );
}

/**
 * Entry point deciding on the structure of the app
 */
export function App(): JSX.Element {
  if (runtimeConfig.features.muiProKey) {
    LicenseInfo.setLicenseKey(runtimeConfig.features.muiProKey);
  }

  const sidebar = useAppSelector(sidebarSelector);

  useErrorHandler();

  useHandleWindowClose();

  useDocumentTitle();

  useInitAppAnalytics();

  return (
    <AllAppLayoutContextProviders>
      <div className="App" data-testid="app-container">
        <Box component="div" sx={{ display: "flex", height: "100vh" }}>
          {sidebar.isVisible && <Sidebar />}
          {/* <Outlet> is a placeholder for the active child route defined in the routes.ts */}
          <Outlet />
          <SplashScreenManager />
        </Box>
      </div>
    </AllAppLayoutContextProviders>
  );
}
